import React, { useEffect, useMemo, useCallback, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { makeStyles } from "@fluentui/react-components";
import { Add16Filled } from "@fluentui/react-icons";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { checkUserPermission } from "../../../../utils";
import { UserPermissionsList } from "../../../../config";
import {
  clearAllAssetsData,
  getAssetListItems,
} from "../../../../redux/Case/assetSlice";
import ASCButton from "../../../../components/Button";
import NoDataFound from "../../../../components/NoDataFound/noDataFound";
import CaseList from "../CaseList";
import { getCurrentStatusParam } from "../../../../shared/caseActiveStatus";
import { ASSET_CASE_TYPE_MAP, ASSET_HAS_STATUS_FIELD } from "./assetSubTypeConfig";

type Phase =
  | "checking-current"      // first fetch: current records only
  | "checking-non-current"  // second fetch: all records (no status filter)
  | "has-non-current-only"  // non-current records exist, no current ones
  | "truly-empty"           // no records at all
  | "navigating";           // current records found, navigate effect will fire

const useStyles = makeStyles({
  wrapper: {
    display: "flex",
    justifyContent: "flex-end",
    margin: "10px 10px 0",
  },
});

const AssetHomePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { clientId, case_type, action } = useParams<{
    clientId: string;
    case_type?: string;
    action?: string;
  }>();

  const { authUser, assets } = useSelector((state: RootState) => ({
    authUser: state.authUser,
    assets: state.assets,
  }));

  const styles = useStyles();

  useEffect(() => {
    if (clientId && !case_type) {
      navigate(`/clients/${clientId}/assets/investment`, { replace: true });
    }
  }, [clientId, case_type, navigate]);

  const dbCaseType = case_type ? ASSET_CASE_TYPE_MAP[case_type] : undefined;
  const hasStatusField = case_type ? ASSET_HAS_STATUS_FIELD[case_type] : true;

  const [phase, setPhase] = useState<Phase>("checking-current");

  // Tracks the in-flight fetch so a stale response (from a sub-type the user
  // has since navigated away from) can't overwrite the current phase/state.
  const requestIdRef = useRef(0);

  // Reset phase whenever the sub-type changes
  useEffect(() => {
    setPhase("checking-current");
  }, [case_type]);

  // Fetch effect — runs only during the two "checking" phases
  useEffect(() => {
    if (!clientId || !case_type) return;
    if (phase !== "checking-current" && phase !== "checking-non-current") return;

    const requestId = ++requestIdRef.current;

    dispatch(clearAllAssetsData());
    dispatch(
      getAssetListItems({
        customerId: clientId,
        caseType: dbCaseType,
        status:
          phase === "checking-current" && hasStatusField
            ? getCurrentStatusParam("asset")
            : undefined,
      })
    )?.then?.((result: any) => {
      if (requestIdRef.current !== requestId) return;
      const items = Array.isArray(result?.payload) ? result.payload : [];
      const filtered = dbCaseType
        ? items.filter((x: any) => x.case_type === dbCaseType)
        : items;

      if (phase === "checking-current") {
        if (filtered.length > 0) {
          setPhase("navigating");
        } else if (!hasStatusField) {
          // Types without a status field have no concept of current/non-current
          setPhase("truly-empty");
        } else {
          // Has status field but no current records — check if non-current exist
          setPhase("checking-non-current");
        }
      } else {
        // phase === "checking-non-current"
        // After this fetch, listItems/matchingItems holds the non-current items,
        // ready to navigate when the user clicks the "Current Cases" toggle.
        setPhase(filtered.length > 0 ? "has-non-current-only" : "truly-empty");
      }
    });
  }, [clientId, case_type, dbCaseType, hasStatusField, phase, dispatch]);

  // matchingItems reflects whatever the last fetch returned (current or non-current)
  const matchingItems = useMemo(
    () =>
      dbCaseType
        ? assets.listItems.filter((item: any) => item.case_type === dbCaseType)
        : [],
    [assets.listItems, dbCaseType]
  );

  // Navigate to first item — only fires when phase explicitly becomes "navigating"
  useEffect(() => {
    if (phase !== "navigating" || !case_type || matchingItems.length === 0) return;
    navigate(
      `/clients/${clientId}/assets/${case_type}/${matchingItems[0]?.asset_id}/asset/${
        action || "view"
      }`
    );
  }, [clientId, case_type, action, navigate, matchingItems, phase]);

  const handleAddNew = useCallback(() => {
    navigate(`/clients/${clientId}/assets/${case_type}/add`);
  }, [clientId, case_type, navigate]);

  // Don't render during loading or active fetching phases
  if (!case_type || assets.loading) return null;
  if (
    phase === "checking-current" ||
    phase === "checking-non-current" ||
    phase === "navigating"
  ) return null;

  const addNewButton = checkUserPermission(
    authUser,
    UserPermissionsList.ALLOW_UPDATE_CLIENT
  ) && (
    <ASCButton
      shape="rounded"
      appearance="primary"
      className="asc-button-primary"
      icon={<Add16Filled />}
      size="medium"
      onClick={handleAddNew}
      label="Add New"
    />
  );

  // Scenario 1: non-current records exist but no current ones.
  // Show the same table with toggle button and a message.
  // Clicking "Current Cases" toggle navigates to the first non-current record
  // (matchingItems is already populated from the checking-non-current fetch).
  if (phase === "has-non-current-only") {
    return (
      <div>
        <div className={styles.wrapper}>{addNewButton}</div>
        <div style={{ marginTop: "10px" }}>
          <CaseList
            currentRecord={1}
            setCurrentRecord={() => {}}
            module="assets"
            data={[]}
            activeItem={null}
            section="assets"
            tab="asset"
            hasStatusField={hasStatusField}
            showNonCurrent={false}
            onToggleNonCurrent={() => setPhase("navigating")}
            emptyMessage='No active records for current cases. Click "Non-current Cases" to view non-current records.'
          />
        </div>
      </div>
    );
  }

  // Scenario 2: no records at all — no table, just NoDataFound
  return (
    <div>
      <div className={styles.wrapper}>{addNewButton}</div>
      <NoDataFound />
    </div>
  );
};

export default AssetHomePage;
